import type { TCommunicationEquipmentChange } from "./communication-types-helper";

export type TCommunicationOriginalEmailSection = {
  id: string;
  type: "header" | "paragraph" | "quote";
  text: string;
  label?: string;
};

type THeaderMatch = {
  label: string;
  start: number;
  end: number;
};

const EMAIL_HEADER_LABELS = [
  "Von",
  "From",
  "Gesendet",
  "Sent",
  "An",
  "To",
  "Cc",
  "Betreff",
  "Subject",
] as const;
const MAX_LEADING_HEADER_SECTIONS = 5;

const GREETING_PATTERN = /\b(?:Sehr geehrte(?:n)?|Guten Tag|Hallo|Hi|Dear)\b/iu;
const SIGN_OFF_PATTERN =
  /\b(?:Mit freundlichen Gr(?:uessen|\u00fc\u00dfen)|Best regards|Kind regards|Regards|Viele Gr(?:uesse|\u00fc\u00dfe)|Beste Gr(?:uesse|\u00fc\u00dfe))\b/iu;
const SIGNATURE_HINT_PATTERN =
  /\b(?:gmbh|marketing|project manager|manager|tel\.?|phone|mobil|mobile|vertretungsberechtigte|gesch\u00e4ftsf\u00fchrer|ceo|director)\b|@|\+\d/iu;
const HIGHLIGHT_STOP_WORDS = new Set([
  "a",
  "am",
  "an",
  "and",
  "auf",
  "bei",
  "das",
  "den",
  "der",
  "die",
  "ein",
  "eine",
  "einen",
  "für",
  "fur",
  "im",
  "in",
  "mit",
  "of",
  "the",
  "to",
  "uber",
  "ueber",
  "und",
  "von",
  "vom",
  "with",
  "zu",
]);

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const decodeEmailEntities = (value: string): string =>
  value
    .replace(/&nbsp;/giu, " ")
    .replace(/&quot;/giu, "\"")
    .replace(/&#39;/giu, "'")
    .replace(/&lt;/giu, "<")
    .replace(/&gt;/giu, ">")
    .replace(/&amp;/giu, "&");

const HEADER_LINE_PATTERN = new RegExp(
  `^(?:${EMAIL_HEADER_LABELS.map(escapeRegExp).join("|")}):`,
  "iu"
);

const normalizeOriginalEmailText = (text: string): string =>
  text
    .replace(/\u00a0/gu, " ")
    .replace(/\s+(?=(?:Von|From|Gesendet|Sent|An|To|Cc|Betreff|Subject):)/giu, "\n")
    .replace(/([.!?])\s+(?=(?:Sehr geehrte(?:n)?|Guten Tag|Hallo|Hi|Dear)\b)/giu, "$1\n\n")
    .replace(/\s+(?=(?:Mit freundlichen Gr(?:uessen|\u00fc\u00dfen)|Best regards|Kind regards|Regards|Viele Gr(?:uesse|\u00fc\u00dfe)|Beste Gr(?:uesse|\u00fc\u00dfe))\b)/giu, "\n\n")
    .replace(/[ \t]+\n/gu, "\n")
    .replace(/\n{3,}/gu, "\n\n")
    .trim();

const isHeaderLine = (line: string): boolean =>
  HEADER_LINE_PATTERN.test(line.trim());

const isSignatureLine = (line: string): boolean =>
  SIGNATURE_HINT_PATTERN.test(line.trim()) && line.trim().length <= 180;

const createSection = (
  id: string,
  type: TCommunicationOriginalEmailSection["type"],
  text: string,
  label?: string
): TCommunicationOriginalEmailSection => ({
  id,
  type,
  text,
  label,
});

const getHeaderMatches = (text: string): THeaderMatch[] => {
  const pattern = new RegExp(`(^|\\s)(${EMAIL_HEADER_LABELS.map(escapeRegExp).join("|")}):`, "giu");
  const matches: THeaderMatch[] = [];

  for (const match of text.matchAll(pattern)) {
    const leadingWhitespace = match[1] || "";
    const label = match[2];
    const labelStart = (match.index || 0) + leadingWhitespace.length;

    matches.push({
      label,
      start: labelStart,
      end: labelStart + label.length + 1,
    });
  }

  return matches;
};

const findBodySplitIndex = (text: string): number => {
  const blankLineMatch = /\n{2,}/u.exec(text);

  if (blankLineMatch?.index !== undefined) {
    return blankLineMatch.index + blankLineMatch[0].length;
  }

  const singleLineBreakMatch = /\n/u.exec(text);

  if (singleLineBreakMatch?.index !== undefined) {
    return singleLineBreakMatch.index + singleLineBreakMatch[0].length;
  }

  const greetingMatch = GREETING_PATTERN.exec(text);

  if (greetingMatch?.index !== undefined && greetingMatch.index > 0) {
    return greetingMatch.index;
  }

  const signOffMatch = SIGN_OFF_PATTERN.exec(text);

  if (signOffMatch?.index !== undefined && signOffMatch.index > 0) {
    return signOffMatch.index;
  }

  return -1;
};

const splitIntoSentences = (text: string): string[] => {
  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "sentence" });

    return Array.from(segmenter.segment(text), ({ segment }) => segment);
  }

  return text.match(/[^.!?]+(?:[.!?]+(?=\s|$)|$)/gu) || [ text ];
};

const splitProseIntoParagraphs = (text: string, prefix: string): TCommunicationOriginalEmailSection[] => {
  const trimmedText = text.trim();

  if (!trimmedText) {
    return [];
  }

  const sentences = splitIntoSentences(trimmedText)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

  if (sentences.length <= 2 && trimmedText.length <= 320) {
    return [ createSection(`${prefix}-0`, "paragraph", trimmedText) ];
  }

  const paragraphs: string[] = [];
  let currentParagraph = "";
  let currentSentenceCount = 0;

  sentences.forEach((sentence) => {
    if (GREETING_PATTERN.test(sentence) || SIGN_OFF_PATTERN.test(sentence)) {
      if (currentParagraph) {
        paragraphs.push(currentParagraph.trim());
        currentParagraph = "";
        currentSentenceCount = 0;
      }

      paragraphs.push(sentence);
      return;
    }

    const nextParagraph = currentParagraph ? `${currentParagraph} ${sentence}` : sentence;

    if (
      currentParagraph
      && (nextParagraph.length > 280 || currentSentenceCount >= 2)
    ) {
      paragraphs.push(currentParagraph.trim());
      currentParagraph = sentence;
      currentSentenceCount = 1;
      return;
    }

    currentParagraph = nextParagraph;
    currentSentenceCount += 1;
  });

  if (currentParagraph) {
    paragraphs.push(currentParagraph.trim());
  }

  return paragraphs.map((paragraph, index) =>
    createSection(`${prefix}-${index}`, "paragraph", paragraph)
  );
};

const buildSectionsFromBlock = (
  block: string,
  blockIndex: number
): TCommunicationOriginalEmailSection[] => {
  const lines = block
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return [];
  }

  const headerLinesCount = lines.filter(isHeaderLine).length;

  if (headerLinesCount >= 2) {
    const sections: TCommunicationOriginalEmailSection[] = [];
    let headerGroup: string[] = [];
    let proseGroup: string[] = [];

    const flushHeaderGroup = () => {
      if (!headerGroup.length) {
        return;
      }

      sections.push(
        createSection(
          `quote-${blockIndex}-${sections.length}`,
          "quote",
          headerGroup.join("\n")
        )
      );
      headerGroup = [];
    };

    const flushProseGroup = () => {
      if (!proseGroup.length) {
        return;
      }

      sections.push(
        ...splitProseIntoParagraphs(
          proseGroup.join(" "),
          `paragraph-${blockIndex}-${sections.length}`
        )
      );
      proseGroup = [];
    };

    lines.forEach((line) => {
      if (isHeaderLine(line)) {
        flushProseGroup();
        headerGroup.push(line);
        return;
      }

      flushHeaderGroup();

      if (isSignatureLine(line) || GREETING_PATTERN.test(line) || SIGN_OFF_PATTERN.test(line)) {
        flushProseGroup();
        sections.push(
          createSection(`paragraph-${blockIndex}-${sections.length}`, "paragraph", line)
        );
        return;
      }

      proseGroup.push(line);
    });

    flushHeaderGroup();
    flushProseGroup();

    return sections;
  }

  if (lines.length > 1) {
    const sections: TCommunicationOriginalEmailSection[] = [];
    let proseGroup: string[] = [];

    const flushProseGroup = () => {
      if (!proseGroup.length) {
        return;
      }

      sections.push(
        ...splitProseIntoParagraphs(
          proseGroup.join(" "),
          `paragraph-${blockIndex}-${sections.length}`
        )
      );
      proseGroup = [];
    };

    lines.forEach((line) => {
      if (GREETING_PATTERN.test(line) || SIGN_OFF_PATTERN.test(line) || isSignatureLine(line)) {
        flushProseGroup();
        sections.push(
          createSection(`paragraph-${blockIndex}-${sections.length}`, "paragraph", line)
        );
        return;
      }

      proseGroup.push(line);
    });

    flushProseGroup();

    return sections;
  }

  return splitProseIntoParagraphs(block, `paragraph-${blockIndex}`);
};

const buildParagraphSections = (text: string): TCommunicationOriginalEmailSection[] => {
  const trimmedText = normalizeOriginalEmailText(text);

  if (!trimmedText) {
    return [];
  }

  const blocks = trimmedText
    .split(/\n{2,}/u)
    .map((block) => block.trim())
    .filter(Boolean);

  if (blocks.length > 1) {
    return blocks.flatMap((block, index) => buildSectionsFromBlock(block, index));
  }

  return buildSectionsFromBlock(trimmedText, 0);
};

export const buildCommunicationOriginalEmailSections = (
  originalBody?: string | null
): TCommunicationOriginalEmailSection[] => {
  const sourceText = decodeEmailEntities((originalBody || "").replace(/\r\n/g, "\n")).trim();

  if (!sourceText) {
    return [];
  }

  const headerMatches = getHeaderMatches(sourceText);

  if (
    !headerMatches.length
    || sourceText.slice(0, headerMatches[0].start).trim()
  ) {
    return buildParagraphSections(sourceText);
  }

  const sections: TCommunicationOriginalEmailSection[] = [];
  let bodyStart = sourceText.length;

  for (let index = 0; index < headerMatches.length; index += 1) {
    const match = headerMatches[index];
    const nextMatch = headerMatches[index + 1];
    const rawValue = sourceText
      .slice(match.end, nextMatch ? nextMatch.start : sourceText.length)
      .trim();

    if (!rawValue) {
      continue;
    }

    if (sections.length >= MAX_LEADING_HEADER_SECTIONS) {
      bodyStart = match.start;
      break;
    }

    const isSubjectHeader = [ "betreff", "subject" ].includes(match.label.toLowerCase());

    if (isSubjectHeader) {
      const splitIndex = findBodySplitIndex(rawValue);

      if (splitIndex > -1) {
        const subjectValue = rawValue.slice(0, splitIndex).trim();

        if (subjectValue) {
          sections.push({
            id: `header-${index}`,
            type: "header",
            label: match.label,
            text: subjectValue,
          });
        }

        bodyStart = match.end + splitIndex;
        break;
      }

      sections.push({
        id: `header-${index}`,
        type: "header",
        label: match.label,
        text: rawValue,
      });
      bodyStart = nextMatch ? nextMatch.start : sourceText.length;
      break;
    }

    if (rawValue.length > 220 && sections.length >= 2) {
      bodyStart = match.start;
      break;
    }

    sections.push({
      id: `header-${index}`,
      type: "header",
      label: match.label,
      text: rawValue,
    });
    bodyStart = nextMatch ? nextMatch.start : sourceText.length;
  }

  const bodyText = sourceText.slice(bodyStart).trim();

  return sections.concat(buildParagraphSections(bodyText));
};

export const collectCommunicationOriginalEmailHighlights = (
  equipmentChanges: TCommunicationEquipmentChange[]
): string[] => {
  const seen = new Set<string>();
  const expandHighlightCandidates = (value: string): string[] => {
    const normalizedValue = value.trim();

    if (!normalizedValue) {
      return [];
    }

    const splitCandidates = normalizedValue
      .split(/[/|,;()\n]+/u)
      .map((item) => item.trim())
      .filter((item) => item.length >= 3);

    const phraseCandidates: string[] = [];

    if (normalizedValue.length > 32) {
      const tokens = normalizedValue
        .split(/\s+/u)
        .map((token) => token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ""))
        .filter(Boolean);

      for (let windowSize = 5; windowSize >= 3; windowSize -= 1) {
        for (let index = 0; index <= tokens.length - windowSize; index += 1) {
          const tokenWindow = tokens.slice(index, index + windowSize);
          const significantTokens = tokenWindow.filter((token) =>
            token.length >= 4 && !HIGHLIGHT_STOP_WORDS.has(token.toLocaleLowerCase())
          );

          if (significantTokens.length < 2) {
            continue;
          }

          const phrase = tokenWindow.join(" ");

          if (phrase.length >= 8 && phrase.length <= 48) {
            phraseCandidates.push(phrase);
          }
        }
      }
    }

    return [ normalizedValue, ...splitCandidates, ...phraseCandidates ];
  };

  return equipmentChanges
    .flatMap((change) => [ change.item_label, change.equipment_title, change.notes ])
    .map((value) => value?.trim() || "")
    .flatMap((value) => expandHighlightCandidates(value))
    .filter((value) => value.length >= 3 && value.length <= 120)
    .filter((value) => {
      const normalizedValue = value.toLocaleLowerCase();

      if (seen.has(normalizedValue)) {
        return false;
      }

      seen.add(normalizedValue);
      return true;
    })
    .sort((left, right) => right.length - left.length);
};
